import { Link, useNavigate, useParams } from "react-router-dom";
import { Account } from "../../models/Account";
import { fetchAccount, fetchCalendarUrl, rotateCalendarToken, updateAccount } from "../../services/AccountService";
import { useState, useEffect, useContext, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
    AlternateEmail, ArrowBack, Badge as BadgeIcon, Cake, CalendarMonth, CheckCircle, ContentCopy,
    ErrorOutline, Forum, Person, PhotoCamera, Save, Share, Shield, SmartDisplay, SportsEsports,
    Tag, Videocam, VisibilityOutlined, Whatshot
} from "@mui/icons-material";
import { AuthContext } from "../../context/AuthContext";
import { admins, roles } from "../../Constants";
import { getDiscordStatus, startDiscordLink, unlinkDiscord, setDiscordVisibility, DiscordStatus } from "../../services/DiscordService";
import { BADGE_TYPES, BadgeType, addBadge, removeBadge, getBadgesForPlayer } from "../../services/BadgeService";
import PlayerImage from "../../components/PlayerImage";
import styles from './EditPlayerPage.module.css';

const parseAge = (age: unknown): number | null => {
    const parsed = parseInt(String(age), 10);
    return !isNaN(parsed) && parsed > 0 ? parsed : null;
};

const Switch = ({ on, onToggle, disabled }: { on: boolean; onToggle: () => void; disabled?: boolean }) => (
    <div
        className={`${styles.switch} ${on ? styles.switchOn : ''} ${disabled ? styles.switchDisabled : ''}`}
        onClick={disabled ? undefined : onToggle}
        role="switch"
        aria-checked={on}
    >
        <div className={styles.knob} />
    </div>
);

const EditPlayerPage = () => {
    const { steamId } = useParams();
    const { t, i18n } = useTranslation('pages');
    const { user: visitor, token, impersonate } = useContext(AuthContext);
    const [player, setPlayer] = useState<Account | null>(null);
    const [editPermission, setEditPermission] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [formData, setFormData] = useState<Partial<Account>>({});
    const [saving, setSaving] = useState<boolean>(false);
    const [calendarUrl, setCalendarUrl] = useState<string | null>(null);
    const [calendarLoading, setCalendarLoading] = useState<boolean>(false);
    const [calendarMessage, setCalendarMessage] = useState<string | null>(null);
    const [discordStatus, setDiscordStatus] = useState<DiscordStatus | null>(null);
    const [discordMessage, setDiscordMessage] = useState<string | null>(null);
    const [discordLoading, setDiscordLoading] = useState<boolean>(false);
    const [playerBadges, setPlayerBadges] = useState<string[] | null>(null);
    const [badgeBusy, setBadgeBusy] = useState<string | null>(null);
    const [toast, setToast] = useState<{ text: string; error?: boolean } | null>(null);
    const toastTimer = useRef<number | undefined>(undefined);
    const navigate = useNavigate();
    const isOwnProfile = !!visitor && visitor.steamID === steamId;

    const showToast = (text: string, error = false) => {
        if (toastTimer.current) window.clearTimeout(toastTimer.current);
        setToast({ text, error });
        toastTimer.current = window.setTimeout(() => setToast(null), 2600);
    };

    useEffect(() => {
        const fetchPlayer = async () => {
            if (!steamId) return;
            try {
                const data = await fetchAccount(steamId);
                if (data) {
                    setPlayer(data);
                    // Initialize form data with original player data
                    setFormData(data);
                }
            } catch (error) {
                console.error('Error fetching player data:', error);
                setPlayer(null);
            }
        };

        fetchPlayer();
    }, [steamId]);

    useEffect(() => {
        if (!visitor) return;
        if (admins.includes(visitor.steamID)) {
            setIsAdmin(true);
        }
    }, [visitor]);

    useEffect(() => {
        if (!visitor) return;
        if (visitor.steamID === steamId || isAdmin) {
            setEditPermission(true);
        }
    }, [visitor, steamId, isAdmin]);

    useEffect(() => {
        if (!isAdmin || !steamId) return;
        let cancelled = false;
        (async () => {
            const data = await getBadgesForPlayer(steamId);
            if (!cancelled) setPlayerBadges(data?.badges ?? []);
        })();
        return () => { cancelled = true; };
    }, [isAdmin, steamId]);

    useEffect(() => {
        if (!isOwnProfile || !token) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchCalendarUrl(token);
                if (!cancelled) setCalendarUrl(data.url);
            } catch (err) {
                console.error("Failed to load calendar URL", err);
            }
        })();
        return () => { cancelled = true; };
    }, [isOwnProfile, token]);

    const showCalendarMessage = (msg: string) => {
        setCalendarMessage(msg);
        window.setTimeout(() => setCalendarMessage(null), 3000);
    };

    const handleCopyCalendarUrl = async () => {
        if (!calendarUrl) return;
        try {
            await navigator.clipboard.writeText(calendarUrl);
            showCalendarMessage(t('editPlayer.calendarCopied'));
        } catch {
            showCalendarMessage(t('editPlayer.calendarCopyError'));
        }
    };

    const handleRotateCalendarUrl = async () => {
        if (!token) return;
        const ok = window.confirm(t('editPlayer.calendarRotateConfirm'));
        if (!ok) return;
        setCalendarLoading(true);
        try {
            const data = await rotateCalendarToken(token);
            setCalendarUrl(data.url);
            showCalendarMessage(t('editPlayer.calendarRotated'));
        } catch (err) {
            console.error("Failed to rotate calendar token", err);
            showCalendarMessage(t('editPlayer.calendarRotateError'));
        } finally {
            setCalendarLoading(false);
        }
    };

    const googleCalendarHref = calendarUrl
        ? `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(calendarUrl)}`
        : "#";

    // ---- Discord linking ----
    useEffect(() => {
        if (!isOwnProfile || !token) return;
        let cancelled = false;
        (async () => {
            const status = await getDiscordStatus();
            if (!cancelled) setDiscordStatus(status);
        })();

        // Surface the result of a just-completed link round trip (?discord=...).
        const result = new URLSearchParams(window.location.search).get("discord");
        if (result) {
            const messageKeys: Record<string, string> = {
                linked: 'editPlayer.discordLinked',
                taken: 'editPlayer.discordTaken',
                cancelled: 'editPlayer.discordCancelled',
                error: 'editPlayer.discordError',
            };
            setDiscordMessage(messageKeys[result] ? t(messageKeys[result]) : null);
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOwnProfile, token]);

    const handleConnectDiscord = async () => {
        setDiscordLoading(true);
        const authorizeUrl = await startDiscordLink();
        if (authorizeUrl) {
            window.location.href = authorizeUrl;
        } else {
            setDiscordMessage(t('editPlayer.discordStartError'));
            setDiscordLoading(false);
        }
    };

    const handleUnlinkDiscord = async () => {
        const ok = window.confirm(t('editPlayer.discordUnlinkConfirm'));
        if (!ok) return;
        setDiscordLoading(true);
        const success = await unlinkDiscord();
        if (success) {
            setDiscordStatus(await getDiscordStatus());
            setDiscordMessage(t('editPlayer.discordUnlinked'));
        } else {
            setDiscordMessage(t('editPlayer.discordUnlinkError'));
        }
        setDiscordLoading(false);
    };

    const handleToggleDiscordPublic = async () => {
        if (!discordStatus || discordLoading) return;
        const isPublic = !discordStatus.isPublic;
        setDiscordLoading(true);
        const ok = await setDiscordVisibility(isPublic);
        if (ok) {
            setDiscordStatus(prev => prev ? { ...prev, isPublic } : prev);
            setDiscordMessage(isPublic ? t('editPlayer.discordPublicOn') : t('editPlayer.discordPublicOff'));
        } else {
            setDiscordMessage(t('editPlayer.discordVisibilityError'));
        }
        setDiscordLoading(false);
    };

    const setField = <K extends keyof Account>(key: K, value: Account[K]) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    // Badges are a different resource from the account flags above, with their own endpoints, so
    // they apply on toggle rather than waiting for Save — a half-saved state split across two APIs
    // is worse than immediate feedback.
    const handleToggleBadge = async (badgeType: BadgeType) => {
        if (!steamId || playerBadges === null || badgeBusy) return;

        const hadBadge = playerBadges.includes(badgeType);
        setBadgeBusy(badgeType);
        // Optimistic flip, reverted below if the call fails.
        setPlayerBadges(prev => (prev ?? []).filter(b => b !== badgeType).concat(hadBadge ? [] : [badgeType]));

        try {
            if (hadBadge) {
                await removeBadge(steamId, badgeType);
            } else {
                await addBadge(steamId, badgeType);
            }
            showToast(t(hadBadge ? 'editPlayer.badgeRemoved' : 'editPlayer.badgeAdded'));
        } catch (err) {
            setPlayerBadges(prev => (prev ?? []).filter(b => b !== badgeType).concat(hadBadge ? [badgeType] : []));
            showToast(err instanceof Error ? err.message : t('editPlayer.badgeError'), true);
        } finally {
            setBadgeBusy(null);
        }
    };

    const validateSocialMediaInputs = (twitter: string, twitch: string, youtube: string, instagram: string) => {
        if (twitter?.includes("twitter.com") || twitter?.includes("x.com")) {
            showToast(t('editPlayer.socialUrlError', { network: 'Twitter' }), true);
            return false;
        }
        if (twitch?.includes("twitch.tv")) {
            showToast(t('editPlayer.socialUrlError', { network: 'Twitch' }), true);
            return false;
        }
        if (youtube?.includes("youtube.com")) {
            showToast(t('editPlayer.socialUrlError', { network: 'YouTube' }), true);
            return false;
        }
        if (instagram?.includes("instagram.com")) {
            showToast(t('editPlayer.socialUrlError', { network: 'Instagram' }), true);
            return false;
        }
        return true;
    }

    const savePlayer = async (updatedData: Account) => {
        try {
            if (!token) {
                showToast(t('editPlayer.loginRequired'), true);
                navigate(`/player/${steamId}`);
                return;
            }
            setSaving(true);
            await updateAccount(updatedData, token);
            showToast(t('editPlayer.saved'));
            window.setTimeout(() => navigate(`/player/${steamId}`), 1100);
        }
        catch (error) {
            setSaving(false);
            showToast(t('editPlayer.saveError'), true);
        }
    }

    const saveChanges = () => {
        if (!player || saving) return;

        // Create updated player object from the controlled form state
        const updatedPlayer: Account = {
            ...player,
            lft: formData.lft ?? player.lft,
            twitter: formData.twitter ?? player.twitter ?? "",
            twitch: formData.twitch ?? player.twitch ?? "",
            youtube: formData.youtube ?? player.youtube ?? "",
            instagram: formData.instagram ?? player.instagram ?? "",
            role: formData.role ?? player.role ?? ""
        };

        // Only update name if this is not an admin editing someone else's profile
        if (isAdmin && visitor?.steamID !== steamId) {
            updatedPlayer.name = player.name;
        } else {
            updatedPlayer.name = formData.name ?? player.name ?? "";
        }

        // Admin-only updates
        if (isAdmin) {
            updatedPlayer.donator = formData.donator ?? player.donator;
            updatedPlayer.premium = formData.premium ?? player.premium;
            updatedPlayer.beta = formData.beta ?? player.beta;

            const playerName = formData.playerName ?? player.playerName;
            if (playerName) {
                updatedPlayer.playerName = playerName;
            }
            updatedPlayer.faceitNickname = formData.faceitNickname ?? player.faceitNickname ?? "";
        }

        if (!validateSocialMediaInputs(updatedPlayer.twitter, updatedPlayer.twitch, updatedPlayer.youtube, updatedPlayer.instagram)) {
            return;
        }

        if (updatedPlayer.lft && !updatedPlayer.role) {
            showToast(t('editPlayer.roleRequired'), true);
            return;
        }

        savePlayer(updatedPlayer);
    }

    const handleCopySteamId = async () => {
        if (!steamId) return;
        try {
            await navigator.clipboard.writeText(steamId);
            showToast(t('editPlayer.steamIdCopied'));
        } catch {
            showToast(t('editPlayer.copyError'), true);
        }
    };

    if (!player) {
        return (
            <div className={styles.page}>
                <p className={styles.stateMessage}>{t('editPlayer.loadError')}</p>
            </div>
        );
    }

    if (!editPermission) {
        return (
            <div className={styles.page}>
                <p className={styles.stateMessage}>{t('editPlayer.noAccess')}</p>
            </div>
        );
    }

    const lft = formData.lft ?? player.lft;
    const role = formData.role ?? player.role ?? "";
    const age = parseAge(player.age);
    const locale = i18n.language === 'da' ? 'da-DK' : 'en-GB';
    const lftSinceDate = player.lftDate
        ? new Date(player.lftDate).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
        : null;

    const socialFields = [
        { key: 'twitter' as const, icon: <AlternateEmail sx={{ fontSize: 16 }} />, prefix: 'twitter.com/' },
        { key: 'twitch' as const, icon: <Videocam sx={{ fontSize: 16 }} />, prefix: 'twitch.tv/' },
        { key: 'youtube' as const, icon: <SmartDisplay sx={{ fontSize: 16 }} />, prefix: 'youtube.com/@' },
        { key: 'instagram' as const, icon: <PhotoCamera sx={{ fontSize: 16 }} />, prefix: 'instagram.com/' },
    ];

    const adminFlags = [
        { key: 'donator' as const, label: t('editPlayer.donatorLabel'), desc: t('editPlayer.donatorDesc') },
        { key: 'premium' as const, label: t('editPlayer.premiumLabel'), desc: t('editPlayer.premiumDesc') },
        { key: 'beta' as const, label: t('editPlayer.betaLabel'), desc: t('editPlayer.betaDesc') },
    ];

    return (
        <div className={styles.page}>
            <Link to={`/player/${steamId}`} className={styles.backLink}>
                <ArrowBack sx={{ fontSize: 18 }} />
                {t('editPlayer.backToProfile')}
            </Link>

            {/* ===== Header ===== */}
            <div className={styles.pageHeader}>
                <div className={styles.headerTop}>
                    <div className={styles.identity}>
                        <div className={styles.avatar}>
                            <PlayerImage
                                id={player.steamID}
                                width={116}
                                imgStyle={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
                            />
                        </div>
                        <div>
                            <div className={styles.titleRow}>
                                <h1 className={styles.title}>{t('editPlayer.title', { name: player.playerName || player.name || steamId })}</h1>
                                {isAdmin && !isOwnProfile && (
                                    <span className={styles.adminPill}>
                                        <Shield sx={{ fontSize: 14 }} />
                                        {t('editPlayer.adminMode')}
                                    </span>
                                )}
                            </div>
                            <div className={styles.steamRow}>
                                <Tag sx={{ fontSize: 15 }} />
                                <span>{steamId}</span>
                                <button className={styles.copyButton} onClick={handleCopySteamId} title={t('editPlayer.copySteamId')}>
                                    <ContentCopy sx={{ fontSize: 15 }} />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className={styles.headerActions}>
                        {isAdmin && !isOwnProfile && steamId && (
                            <button
                                type="button"
                                onClick={() => impersonate(steamId)}
                                title={t('editPlayer.viewAs')}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    background: 'rgba(217,119,6,0.15)', border: '1px solid rgba(217,119,6,0.5)',
                                    color: '#fbbf24', borderRadius: 8, padding: '8px 14px',
                                    fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                                }}
                            >
                                <VisibilityOutlined sx={{ fontSize: 17 }} />
                                {t('editPlayer.viewAs')}
                            </button>
                        )}
                        <Link to={`/player/${steamId}`} className={styles.cancelButton}>{t('editPlayer.cancel')}</Link>
                        <button className={styles.saveButton} onClick={saveChanges} disabled={saving}>
                            <Save sx={{ fontSize: 18 }} />
                            {t('editPlayer.save')}
                        </button>
                    </div>
                </div>

                <div className={styles.chipsRow}>
                    {role && (
                        <span className={styles.infoChip}>
                            <SportsEsports className={styles.infoChipIcon} sx={{ fontSize: 16 }} />
                            {role}
                        </span>
                    )}
                    {age !== null && (
                        <span className={styles.infoChip}>
                            <Cake className={styles.infoChipIcon} sx={{ fontSize: 16 }} />
                            {age} {t('lft.years')}
                        </span>
                    )}
                    {!!player.faceitElo && (
                        <span className={styles.infoChip}>
                            <Whatshot className={styles.infoChipIconAccent} sx={{ fontSize: 16 }} />
                            Faceit ELO {player.faceitElo}
                        </span>
                    )}
                    <span className={`${styles.lftChip} ${lft ? styles.lftChipOn : ''}`}>
                        <span className={styles.lftChipDot} />
                        {lft ? t('editPlayer.lftActive') : t('editPlayer.lftInactive')}
                    </span>
                </div>
            </div>

            {/* ===== Body ===== */}
            <div className={styles.bodyGrid}>

                {/* Main column */}
                <div className={styles.mainCol}>

                    {/* Profil */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <BadgeIcon className={styles.cardHeaderIcon} sx={{ fontSize: 20 }} />
                            <h2 className={styles.cardTitle}>{t('editPlayer.profile')}</h2>
                        </div>
                        <div className={styles.fieldGrid}>
                            <div>
                                <label className={styles.fieldLabel}>{t('editPlayer.displayName')}</label>
                                <input
                                    className={styles.input}
                                    value={formData.name ?? player.name ?? ""}
                                    onChange={(e) => setField('name', e.target.value)}
                                    disabled={isAdmin && !isOwnProfile}
                                    title={isAdmin && !isOwnProfile ? t('editPlayer.nameAdminLocked') : undefined}
                                />
                            </div>
                            <div>
                                <label className={styles.fieldLabel}>{t('editPlayer.role')}</label>
                                <select
                                    className={styles.select}
                                    value={role}
                                    onChange={(e) => setField('role', e.target.value)}
                                >
                                    <option value="" disabled>{t('editPlayer.chooseRole')}</option>
                                    {roles.map((r) => (
                                        <option value={r} key={r}>{r}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className={styles.toggleRow}>
                            <div>
                                <div className={styles.toggleRowTitle}>{t('editPlayer.lftTitle')}</div>
                                <div className={styles.toggleRowDesc}>
                                    {t('editPlayer.lftDesc')}
                                    {lft && lftSinceDate && <> · {t('editPlayer.lftSince', { date: lftSinceDate })}</>}
                                </div>
                            </div>
                            <Switch on={lft} onToggle={() => setField('lft', !lft)} />
                        </div>
                    </div>

                    {/* Sociale medier */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <Share className={styles.cardHeaderIcon} sx={{ fontSize: 20 }} />
                            <h2 className={styles.cardTitle}>{t('editPlayer.socials')}</h2>
                        </div>
                        <div className={styles.socialList}>
                            {socialFields.map(({ key, icon, prefix }) => (
                                <div className={styles.socialRow} key={key}>
                                    <span className={styles.socialPrefix}>{icon}{prefix}</span>
                                    <input
                                        className={styles.socialInput}
                                        value={formData[key] ?? player[key] ?? ""}
                                        onChange={(e) => setField(key, e.target.value)}
                                        placeholder={t('editPlayer.socialPlaceholder')}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                </div>

                {/* Sidebar */}
                <div className={styles.sideCol}>

                    {/* Admin controls */}
                    {isAdmin && (
                        <div className={styles.adminCard}>
                            <div className={styles.adminCardHeader}>
                                <Shield className={styles.adminCardIcon} sx={{ fontSize: 20 }} />
                                <h2 className={styles.adminCardTitle}>{t('editPlayer.adminControls')}</h2>
                                <span className={styles.adminCardBadge}>{t('editPlayer.adminOnly')}</span>
                            </div>
                            <div className={styles.adminFlags}>
                                {adminFlags.map(({ key, label, desc }) => (
                                    <div className={styles.flagRow} key={key}>
                                        <div>
                                            <div className={styles.flagLabel}>{label}</div>
                                            <div className={styles.flagDesc}>{desc}</div>
                                        </div>
                                        <Switch
                                            on={(formData[key] ?? player[key]) as boolean}
                                            onToggle={() => setField(key, !(formData[key] ?? player[key]))}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className={styles.adminOverrides}>
                                <div className={styles.adminSectionLabel}>{t('editPlayer.badgesSection')}</div>
                                <div className={styles.adminFieldHint} style={{ marginBottom: 10 }}>
                                    {t('editPlayer.badgesHint')}
                                </div>
                                {playerBadges === null ? (
                                    <div className={styles.adminFieldHint}>{t('editPlayer.badgesLoading')}</div>
                                ) : (
                                    <div className={styles.adminFlags}>
                                        {BADGE_TYPES.map((badgeType) => (
                                            <div className={styles.flagRow} key={badgeType}>
                                                <div>
                                                    <div className={styles.flagLabel}>{t(`editPlayer.badge_${badgeType}`)}</div>
                                                    <div className={styles.flagDesc}>{t(`editPlayer.badge_${badgeType}_desc`)}</div>
                                                </div>
                                                <Switch
                                                    on={playerBadges.includes(badgeType)}
                                                    disabled={badgeBusy !== null}
                                                    onToggle={() => handleToggleBadge(badgeType as BadgeType)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className={styles.adminOverrides}>
                                <div className={styles.adminSectionLabel}>{t('editPlayer.overpassOverrides')}</div>
                                <div className={styles.adminField}>
                                    <label className={styles.adminFieldLabel}>{t('editPlayer.gamertag')}</label>
                                    <input
                                        className={styles.inputSmall}
                                        value={formData.playerName ?? player.playerName ?? ""}
                                        onChange={(e) => setField('playerName', e.target.value)}
                                        placeholder={t('editPlayer.gamertagPlaceholder')}
                                    />
                                    <div className={styles.adminFieldHint}>{t('editPlayer.gamertagHint')}</div>
                                </div>
                                <div className={styles.adminField}>
                                    <label className={styles.adminFieldLabel}>{t('editPlayer.faceitNickname')}</label>
                                    <input
                                        className={styles.inputSmall}
                                        value={formData.faceitNickname ?? player.faceitNickname ?? ""}
                                        onChange={(e) => setField('faceitNickname', e.target.value)}
                                        placeholder={t('editPlayer.faceitPlaceholder')}
                                    />
                                    <div className={styles.adminFieldHint}>{t('editPlayer.faceitHint')}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Discord */}
                    {isOwnProfile && discordStatus?.linkingEnabled && (
                        <div className={styles.sideCard}>
                            <div className={styles.sideCardHeader}>
                                <Forum className={styles.discordIcon} sx={{ fontSize: 19 }} />
                                <h2 className={styles.sideCardTitle}>Discord</h2>
                                {discordStatus.linked && (
                                    <span className={styles.connectedBadge}>
                                        <CheckCircle sx={{ fontSize: 14 }} />
                                        {t('editPlayer.discordConnected')}
                                    </span>
                                )}
                            </div>
                            {discordStatus.linked ? (
                                <>
                                    <div className={styles.statusList}>
                                        <div className={styles.statusRow}>
                                            <span className={styles.statusLabel}>{t('editPlayer.discordUsername')}</span>
                                            <span className={styles.statusValue}>{discordStatus.discordUsername}</span>
                                        </div>
                                        <div className={styles.statusRow}>
                                            <span className={styles.statusLabel}>{t('editPlayer.discordOnServer')}</span>
                                            {discordStatus.present
                                                ? <span className={`${styles.statusValue} ${styles.statusValueGood}`}>{t('editPlayer.yes')}</span>
                                                : <span className={`${styles.statusValue} ${styles.statusValueMuted}`}>{t('editPlayer.discordNotSeen')}</span>}
                                        </div>
                                        <div className={styles.statusRow}>
                                            <span className={styles.statusLabel}>{t('editPlayer.discordPublicLabel')}</span>
                                            <Switch on={discordStatus.isPublic} onToggle={handleToggleDiscordPublic} disabled={discordLoading} />
                                        </div>
                                    </div>
                                    <button className={styles.dangerLink} onClick={handleUnlinkDiscord} disabled={discordLoading}>
                                        {discordLoading ? "…" : t('editPlayer.discordUnlink')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <p className={styles.sideCardDesc}>{t('editPlayer.discordDesc')}</p>
                                    <button className={styles.discordConnectButton} onClick={handleConnectDiscord} disabled={discordLoading}>
                                        {discordLoading ? "…" : t('editPlayer.discordConnect')}
                                    </button>
                                </>
                            )}
                            {discordMessage && <div className={styles.sideCardMessage}>{discordMessage}</div>}
                        </div>
                    )}

                    {/* Calendar */}
                    {isOwnProfile && (
                        <div className={styles.sideCard}>
                            <div className={styles.sideCardHeader}>
                                <CalendarMonth className={styles.calendarIcon} sx={{ fontSize: 19 }} />
                                <h2 className={styles.sideCardTitle}>{t('editPlayer.calendar')}</h2>
                            </div>
                            <p className={styles.sideCardDesc}>{t('editPlayer.calendarDesc')}</p>
                            <div className={styles.calendarInputRow}>
                                <input
                                    className={styles.calendarInput}
                                    readOnly
                                    value={calendarUrl ?? t('editPlayer.loading')}
                                    onFocus={(e) => e.currentTarget.select()}
                                />
                                <button
                                    className={styles.smallButton}
                                    onClick={handleCopyCalendarUrl}
                                    disabled={!calendarUrl}
                                    title={t('editPlayer.calendarCopyTitle')}
                                >
                                    {t('editPlayer.copy')}
                                </button>
                                <button
                                    className={styles.smallButton}
                                    onClick={handleRotateCalendarUrl}
                                    disabled={calendarLoading}
                                    title={t('editPlayer.calendarRotateTitle')}
                                >
                                    {calendarLoading ? "…" : t('editPlayer.newLink')}
                                </button>
                            </div>
                            {calendarUrl && (
                                <a
                                    href={googleCalendarHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.calendarGoogleLink}
                                >
                                    {t('editPlayer.addToGoogle')}
                                </a>
                            )}
                            {calendarMessage && <div className={styles.sideCardMessage}>{calendarMessage}</div>}
                            <div className={styles.calendarHint}>{t('editPlayer.calendarHint')}</div>
                        </div>
                    )}

                    {/* Quick actions */}
                    <div className={styles.quickActions}>
                        <Link to={`/player/${steamId}`} className={styles.quickAction}>
                            <Person className={styles.quickActionIcon} sx={{ fontSize: 19 }} />
                            {t('editPlayer.viewPublicProfile')}
                        </Link>
                    </div>

                </div>
            </div>

            {/* Toast — portaled to body so the page wrapper's fadeUp animation (transform)
                can't become the containing block for position:fixed */}
            {toast && createPortal(
                <div className={`${styles.toast} ${toast.error ? styles.toastError : ''}`}>
                    {toast.error
                        ? <ErrorOutline className={styles.toastIconError} sx={{ fontSize: 20 }} />
                        : <CheckCircle className={styles.toastIconOk} sx={{ fontSize: 20 }} />}
                    <span className={styles.toastText}>{toast.text}</span>
                </div>,
                document.body
            )}
        </div>
    );
}

export default EditPlayerPage;
